import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import { supabase } from "@/lib/supabaseClient";
import { clearModuleCache } from "@/lib/hasModuleAccess";
import ErrorBoundary from "@/components/system/ErrorBoundary";

interface AdminLayoutProps {
  children: React.ReactNode;
}

/* ── Keys ─────────────────────────────────────────────────────── */
const KEY_ID   = "impersonated_tenant_id";
const KEY_NAME = "impersonated_tenant_name";
const KEY_SLUG = "impersonated_tenant_slug";
const KEY_FROM = "impersonated_from_sa";

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedName, setImpersonatedName] = useState("");
  const navigate = useNavigate();

  /* ── Auth + impersonation check ─────────────────────────────── */
  useEffect(() => {
    const checkAccess = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate("/login", { replace: true });
        return;
      }
      const { data: sa } = await supabase
        .from("saas_super_admins")
        .select("id")
        .eq("user_id", sessionData.session.user.id)
        .maybeSingle();

      // SA without active impersonation shouldn't be in /admin
      if (sa && !localStorage.getItem(KEY_ID)) {
        navigate("/sa/dashboard", { replace: true });
        return;
      }
    };
    checkAccess();
  }, [navigate]);

  /* ── Read impersonation state from localStorage ──────────────── */
  const refreshImpersonation = useCallback(() => {
    const tid  = localStorage.getItem(KEY_ID);
    const name = localStorage.getItem(KEY_NAME);
    if (tid && name) {
      setIsImpersonating(true);
      setImpersonatedName(name);
    } else {
      setIsImpersonating(false);
      setImpersonatedName("");
    }
  }, []);

  useEffect(() => {
    refreshImpersonation();
    // Listen for storage changes from other tabs
    const handler = () => refreshImpersonation();
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [refreshImpersonation]);

  /* ── Exit impersonation ──────────────────────────────────────── */
  const handleExit = () => {
    localStorage.removeItem(KEY_ID);
    localStorage.removeItem(KEY_NAME);
    localStorage.removeItem(KEY_SLUG);
    localStorage.removeItem(KEY_FROM);
    clearModuleCache();  // Reset module cache so next impersonation starts fresh
    navigate("/sa/tenants");
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f3f7fb]">

      {/* ── Impersonation banner ──────────────────────────────── */}
      {isImpersonating && (
        <div className="sticky top-0 z-[60] w-full bg-violet-600 text-white">
          <div className="flex items-center justify-between gap-4 px-4 py-2.5">
            {/* Left: icon + label */}
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/15 shrink-0">
                <i className="ri-spy-line text-xs"></i>
              </div>
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="text-violet-200 text-xs font-medium whitespace-nowrap">
                  Super Admin impersonation
                </span>
                <span className="w-1 h-1 rounded-full bg-violet-400 shrink-0"></span>
                <span className="text-white text-xs font-bold truncate">
                  Viewing as: {impersonatedName}
                </span>
              </div>
            </div>

            {/* Right: warning pill + exit */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/50 border border-violet-400/40 text-violet-200 text-[10px] font-medium whitespace-nowrap">
                <i className="ri-error-warning-line text-xs"></i>
                Read-only browsing
              </span>
              <button
                onClick={handleExit}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 border border-white/25 rounded-lg text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line text-xs"></i>
                Exit impersonation
              </button>
            </div>
          </div>

          {/* Bottom progress stripe */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-violet-300/40 to-transparent" />
        </div>
      )}

      {/* ── Main layout ──────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Mobile backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <AdminSidebar isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        {/* Main content */}
        <main className="flex-1 overflow-auto min-w-0">
          {/* Mobile-only sticky top bar */}
          <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="w-9 h-9 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-slate-700 transition-colors cursor-pointer shrink-0"
              aria-label="Open menu"
            >
              <i className="ri-menu-2-line text-base"></i>
            </button>
            <img
              src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/6cb7fb8d08f57254bfb69080e4b6eb8a.png"
              alt="Pixory AI"
              className="h-6 w-auto object-contain"
            />
            <span className="text-[#0f172a] text-xs font-semibold tracking-widest">Pixory AI</span>

            {/* Mobile impersonation indicator */}
            {isImpersonating && (
              <div className="ml-auto flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse"></span>
                <span className="text-violet-600 text-[10px] font-semibold">SA mode</span>
              </div>
            )}
          </div>

          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
